// SC-A1c — homework template attachment carry (copy-on-use).
//
// Model (b), locked: template attachments live as INDEPENDENT objects the specialist owns, under
// their existing Pattern-E prefix `specialists/<auth-uid>/templates/…` (RLS: owner-only, op-agnostic,
// NO policy change). On USE, each attachment is copied to the target child's `homework/<childId>/…`
// path so the receiving parent can sign it. Raw cross-child path reuse is the bug this removes
// (report 03/04, Q2 — parent is RLS-denied on another child's path).
//
// Primitive: `_supa.storage.from('huddledin-files').copy(src, dest)` — server-side, same-bucket, no
// bytes cross the client (matters for videos). RLS smoke-verified BOTH directions live as a specialist
// (2026-09-08). Paths only — `attachedFileUrls` is always cleared to [] (lazy-sign at render; never
// persist an expiring URL). Best-effort per file: a copy failure drops that one attachment and is
// reported; the homework/template still saves.

const BUCKET = 'huddledin-files';

function _basename(p) {
  const b = (p || '').split('/').pop() || 'file';
  return b.replace(/^\d+_/, ''); // strip a leading "<timestamp>_" if present
}
function _safeName(n) { return (n || 'file').replace(/[\\/]/g, '_'); }

async function _copyOne(H, src, dest) {
  try {
    const { error } = await H._supa.storage.from(BUCKET).copy(src, dest);
    return { ok: !error, error };
  } catch (e) { return { ok: false, error: e }; }
}

// Max-4 concurrency runner (mirrors the _signFilePool precedent). `seq` is closed over so every
// destination is unique even when Date.now() collides across the parallel window.
async function _runPool(jobs, buildDest, H, max = 4) {
  let seq = 0;
  let ptr = 0;
  async function worker() {
    while (ptr < jobs.length) {
      const j = jobs[ptr++];
      const dest = buildDest(j, seq++);
      const r = await _copyOne(H, j.src, dest);
      j.ok = r.ok; j.dest = r.ok ? dest : null;
      if (!r.ok) console.error('❌ attachment copy:', j.src, '→', dest, r.error);
    }
  }
  await Promise.all(Array.from({ length: Math.min(max, jobs.length) }, worker));
}

// SAVE-AS (touch-points 1 & 2): copy source objects into the specialist's own template prefix and
// return the template-owned {paths, names}. Failed copies are dropped. Never throws.
export async function copyAttachmentsToTemplate(paths, names, H) {
  const src = paths || [], nm = names || [];
  if (!src.length) return { paths: [], names: [] };
  const uid = H.session?.id; // pin to auth uid (Pattern E segment[2]), NOT specialistId
  const prefix = 'specialists/' + uid + '/templates/';
  const outP = [], outN = [];
  const jobs = [];
  src.forEach((p, i) => {
    if (!p) return;
    const name = nm[i] || _basename(p); // align names by ORIGINAL index
    // Already under THIS specialist's template prefix (e.g. add-from-library then 💾 on the same
    // exercise): keep the shared owner-scoped object as-is — no re-copy. Mirrors carryAttachmentsToChild's
    // skip. Independence only matters CROSS-CHILD; intra-owner sharing is safe (orphan-on-delete posture).
    if (p.startsWith(prefix)) { outP.push(p); outN.push(name); return; }
    jobs.push({ src: p, name });
  });
  await _runPool(jobs, (j, s) => prefix + Date.now() + '_' + s + '_' + _safeName(j.name), H);
  jobs.forEach(j => { if (j.dest) { outP.push(j.dest); outN.push(j.name); } });
  return { paths: outP, names: outN };
}

// USE (inside _doSave, before the isEdit fork): copy every exercise's non-target attachment onto the
// target child's homework path. Mutates each exercise in place (paths rewritten to the child path,
// failures dropped, urls cleared). Returns the titles of exercises that lost ≥1 attachment. Never throws.
export async function carryAttachmentsToChild(exercises, childId, H) {
  const prefix = 'homework/' + childId + '/';
  const jobs = [];
  (exercises || []).forEach(ex => {
    const paths = ex.attachedFilePaths || [], names = ex.attachedFileNames || [];
    paths.forEach((p, i) => {
      // Skip paths already under the target child (also no-ops "add-from-library then save on the
      // same exercise" and any re-save of an already-carried homework).
      if (p && !p.startsWith(prefix)) jobs.push({ ex, idx: i, src: p, name: names[i] || _basename(p) });
    });
  });
  if (!jobs.length) return [];
  await _runPool(jobs, (j, s) => prefix + Date.now() + '_' + s + '_' + _safeName(j.name), H);
  const failed = new Set();
  jobs.forEach(j => {
    if (j.ok) j.ex.attachedFilePaths[j.idx] = j.dest;
    else { j.ex.attachedFilePaths[j.idx] = null; failed.add(j.ex); }
  });
  const warnings = [];
  (exercises || []).forEach(ex => {
    if (!ex.attachedFilePaths) return;
    const kp = [], kn = [];
    ex.attachedFilePaths.forEach((p, i) => { if (p != null) { kp.push(p); kn.push((ex.attachedFileNames || [])[i]); } });
    ex.attachedFilePaths = kp;
    ex.attachedFileNames = kn;
    ex.attachedFileUrls = []; // lazy-sign at render — never persist an expiring URL
    if (failed.has(ex)) warnings.push(ex.title || '');
  });
  return warnings;
}
